import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Dialog, ModalMask } from '@fx-ui/fine-design';
import { assign, ECode, EMPTY_FN, get, isNumber, loadScript } from '@libs/shared';
import { LOCALE_KEY } from '@fx-ui/intl';
import { EXTERNAL_URL, platformIntl } from '../../constants';
import { globalState } from '../../models';
import { getErrorMsg, post } from '../../utils';

interface RecaptchaDialogProps {
    onClose: () => void;
    onSuccess: () => void;
    data: any;
    url: string;
    visible: boolean;
}

const getLanguage = () => {
    // https://cloud.google.com/recaptcha/docs/language
    const languageMap: Record<LOCALE_KEY, string> = {
        [LOCALE_KEY.ZH_CN]: 'zh-CN',
        [LOCALE_KEY.ZH_TW]: 'zh-TW',
        [LOCALE_KEY.EN_US]: 'en',
        [LOCALE_KEY.JA_JP]: 'ja',
        [LOCALE_KEY.VI_VN]: 'vi',
        [LOCALE_KEY.TH_TH]: 'th',
        [LOCALE_KEY.ID_ID]: 'id',
        [LOCALE_KEY.KO_KR]: 'ko',
        [LOCALE_KEY.DE_DE]: 'de',
        [LOCALE_KEY.ES_ES]: 'es',
        [LOCALE_KEY.FR_FR]: 'fr',
        [LOCALE_KEY.PT_PT]: 'pt-PT',
        [LOCALE_KEY.RU_RU]: 'ru',
        [LOCALE_KEY.KM_KH]: 'en', // reCAPTCHA 不支持高棉语，暂时使用英语
        [LOCALE_KEY.LO_LA]: 'lo',
        [LOCALE_KEY.HI_IN]: 'hi',
        [LOCALE_KEY.BN_BD]: 'bn',
        [LOCALE_KEY.TA_IN]: 'ta',
        [LOCALE_KEY.UZ_UZ]: 'uz',
        [LOCALE_KEY.MN_MN]: 'mn',
        [LOCALE_KEY.IT_IT]: 'it',
        [LOCALE_KEY.NL_NL]: 'nl',
        [LOCALE_KEY.PL_PL]: 'pl',
        [LOCALE_KEY.HU_HU]: 'hu',
        [LOCALE_KEY.PT_BR]: 'pt-BR'
    };
    const currentLocale = globalState.localeKey!;
    return languageMap[currentLocale] || currentLocale;
};

const getRecaptcha = () => (window as any).grecaptcha;

const RecaptchaDialog: React.FC<RecaptchaDialogProps> = (props) => {
    const { visible, onClose, onSuccess, data, url } = props;

    const recaptchaRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<number>();

    const [errorMsg, setErrorMsg] = useState<string>();

    const isMobile = globalState.isMobile;

    useEffect(() => {
        const recaptcha = getRecaptcha();
        if (recaptcha) {
            // 已经加载过 js
            recaptcha.ready(() => {
                initRecaptcha();
            });
        } else {
            (window as any).onloadRecaptchaCallback = () => {
                initRecaptcha();
            };
        }
        loadScript(
            EXTERNAL_URL.GOOGLE_RECAPTCHA_URL +
                `?onload=onloadRecaptchaCallback&render=explicit&hl=${getLanguage()}`,
            EMPTY_FN
        );
        setErrorMsg(undefined);

        return () => {
            (window as any).onloadRecaptchaCallback = null;
        };
    }, []);

    const initRecaptcha = () => {
        // https://developers.google.com/recaptcha/docs/display?hl=zh-cn
        widgetIdRef.current = getRecaptcha()?.render(recaptchaRef.current, {
            sitekey: globalState.options?.captcha?.params?.site_key,
            size: isMobile ? 'compact' : 'normal',
            theme: 'light',
            // 当用户提交成功时执行的回调函数
            callback: (token: string) => {
                doVerify(token);
            },
            'expired-callback': resetRecaptcha,
            'error-callback': () => {
                console.error('reCAPTCHA Enterprise failed to load or execute');
            }
        });
    };

    const resetRecaptcha = () => {
        if (isNumber(widgetIdRef.current)) {
            getRecaptcha()?.reset(widgetIdRef.current);
        }
    };

    const doVerify = (token: string) => {
        post(
            url,
            assign({}, data, {
                captcha_token: token
            }),
            {
                customHandleErrorCodes: [
                    ECode.EMAIL_FORMAT_IS_INVALID,
                    ECode.USER_PHONE_FORMAT_INVALID,
                    ECode.USER_PHONE_REQUIRED,
                    ECode.USER_WRONG_AUTH_CODE_CAPTCHA,
                    ECode.USER_VERIFY_CODE_SEND_LIMIT_EXCEEDED,
                    ECode.USER_PHONE_INVALID,
                    ECode.USER_EMAIL_IS_INCORRECT,
                    ECode.USER_NO_AUTH_CODE_CAPTCHA
                ]
            }
        )
            .then(() => {
                onSuccess();
            })
            .catch((res) => {
                const err = get(res, 'response.data');
                setErrorMsg(getErrorMsg(err));
                resetRecaptcha();
            });
    };

    const renderDialogContent = () => {
        return (
            <>
                <div ref={recaptchaRef} id="recaptcha-wrapper" />
                {errorMsg && (
                    <div className="captcha-error-msg" title={errorMsg}>
                        {errorMsg}
                    </div>
                )}
            </>
        );
    };

    if (isMobile) {
        return (
            <ModalMask visible={visible} maskStyle={{ background: 'rgba(0, 0, 0, .25)' }}>
                <div className="fx-mobile-recaptcha-dialog">
                    <div
                        className={classNames('dialog-content', {
                            error: errorMsg
                        })}
                    >
                        <div className="captcha-title">{platformIntl.get('captcha.title')}</div>
                        {renderDialogContent()}
                    </div>
                    <div className="dialog-footer">
                        <div className="dialog-btn cancel" onClick={onClose}>
                            {platformIntl.get('cancel')}
                        </div>
                    </div>
                </div>
            </ModalMask>
        );
    }

    return (
        <Dialog
            className="fx-recaptcha-dialog"
            visible={visible}
            separator={false}
            width={350}
            height={errorMsg ? 190 : 180}
            title={platformIntl.get('captcha.title')}
            onCancel={onClose}
            text4Ok={null}
            text4Cancel={null}
        >
            {renderDialogContent()}
        </Dialog>
    );
};

export default RecaptchaDialog;
